/**
 * Firestore Analytics Page
 *
 * Real-time database cost and usage analytics including
 * reads, writes, deletes, collection breakdown, and cost projections.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import MetricCard from '../components/analytics/MetricCard';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import {
  DocumentTextIcon,
  PencilSquareIcon,
  TrashIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

export default function FirestorePage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [byCollection, setByCollection] = useState(null);
  const [costProjection, setCostProjection] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, collectionsRes, projectionRes, trendsRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getFirestoreOverview(),
        thirdPartyAnalyticsAPI.getFirestoreByCollection(),
        thirdPartyAnalyticsAPI.getFirestoreCostProjection(),
        thirdPartyAnalyticsAPI.getFirestoreTrends(30),
      ]);
      setOverview(overviewRes.data);
      setByCollection(collectionsRes.data);
      setCostProjection(projectionRes.data);
      setTrends(trendsRes.data);
    } catch (err) {
      console.error('[FirestorePage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv]);

  // Get cost status
  const getCostStatus = () => {
    const cost = overview?.estimatedMonthlyCost || 0;
    if (cost > 100) return 'critical';
    if (cost > 50) return 'warning';
    return 'healthy';
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Firestore Analytics" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading Firestore analytics</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Format large numbers
  const formatCount = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Collection table columns
  const collectionColumns = [
    { key: 'collection', header: 'Collection', sortable: true },
    {
      key: 'reads',
      header: 'Reads',
      sortable: true,
      render: (val) => formatCount(val),
    },
    {
      key: 'writes',
      header: 'Writes',
      sortable: true,
      render: (val) => formatCount(val),
    },
    {
      key: 'deletes',
      header: 'Deletes',
      sortable: true,
      render: (val) => formatCount(val),
    },
    {
      key: 'estimatedCost',
      header: 'Est. Cost',
      sortable: true,
      render: (val) => `$${(val || 0).toFixed(2)}`,
    },
  ];

  // Cost projection trend data - use trends data from /trends endpoint
  const costTrendData = (trends?.trends || []).map(t => ({
    date: t.date,
    cost: t.cost,
  }));
  const costTrendLines = [
    { key: 'cost', name: 'Daily Cost', color: '#F59E0B' },
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="Firestore Analytics"
        description="Real-time database usage and cost metrics"
        onRefresh={fetchData}
        loading={loading}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Reads (30d)"
          value={overview?.totalReads30d}
          icon={DocumentTextIcon}
          loading={loading}
        />
        <MetricCard
          title="Total Writes (30d)"
          value={overview?.totalWrites30d}
          icon={PencilSquareIcon}
          loading={loading}
        />
        <MetricCard
          title="Total Deletes (30d)"
          value={overview?.totalDeletes30d}
          icon={TrashIcon}
          loading={loading}
        />
        <MetricCard
          title="Est. Monthly Cost"
          value={overview?.estimatedMonthlyCost}
          format="currency"
          icon={CurrencyDollarIcon}
          status={getCostStatus()}
          loading={loading}
        />
      </div>

      {/* Operations by Collection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Operations by Collection
        </h3>
        <DataTable
          data={byCollection?.collections || []}
          columns={collectionColumns}
          loading={loading}
          emptyMessage="No collection data available"
        />
      </div>

      {/* Cost Projection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Cost Projection
        </h3>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <>
            <TrendChart
              data={costTrendData}
              lines={costTrendLines}
              height={250}
              xAxisKey="date"
              type="area"
            />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-sm text-amber-700">Current Run Rate</p>
                <p className="text-2xl font-bold text-amber-800">
                  ${(costProjection?.projectedMonthlyCost || 0).toFixed(2)}/month
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Projected Annual</p>
                <p className="text-2xl font-bold text-gray-800">
                  ${((costProjection?.projectedMonthlyCost || 0) * 12).toFixed(2)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cost Calculation Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
        <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Cost Estimate Methodology</p>
          <p className="mt-1 text-blue-700">
            Based on Firestore pricing: Reads $0.036/100K, Writes $0.108/100K, Deletes $0.012/100K.
            Actual costs may vary based on region and data transfer.
          </p>
        </div>
      </div>
    </div>
  );
}
